"use client";

import { isNextClient } from "@perk/core/lib/is-next-client";
import { useContext } from "react";

interface UniversalSearchParams {
  get(key: string): string | null;
  getAll(key: string): string[];
  has(key: string): boolean;
  toString(): string;
}

class TanStackSearchParamsAdapter implements UniversalSearchParams {
  constructor(private searchStr: string) {
    // Convert search string to URLSearchParams
    this.searchStr = searchStr;
  }

  private getParams(): URLSearchParams {
    return new URLSearchParams(this.searchStr);
  }

  get(key: string): string | null {
    return this.getParams().get(key);
  }

  getAll(key: string): string[] {
    return this.getParams().getAll(key);
  }

  has(key: string): boolean {
    return this.getParams().has(key);
  }

  toString(): string {
    return this.searchStr;
  }
}

let _useSearchParams: null | (() => UniversalSearchParams) = null;

if (isNextClient()) {
  const { useSearchParams: useNextSearchParams } = require("next/navigation");
  _useSearchParams = useNextSearchParams;
} else {
  const { useLocation, getRouterContext } = require("@tanstack/react-router");
  _useSearchParams = function useSearchParamsFallback() {
    const router = useContext(getRouterContext());
    const location = useLocation({
      router,
      // @ts-expect-error
      select: (location) => location.searchStr,
    });
    return new TanStackSearchParamsAdapter(location);
  };
}

export function useSearchParams() {
  if (_useSearchParams === null) {
    throw new Error("useSearchParams is not initialized");
  }
  return _useSearchParams();
}
